<template>
  <div class="q-gutter-lg">
    <CTitle large>
      {{ $t('Notifications') }}
    </CTitle>

    <HeadContents :loading="loading" @openModal="() => openModal('')" />

    <DataTable
      :tableData="tableData"
      v-if="notificationSettings.globalEnabled"
      @openModal="openModal"
      @openDeleteModal="openDeleteChannelModal"
      @success="onSuccess"
    />

    <Banner type="info" v-else>{{ $t('Please allow the notification.') }}</Banner>

    <ModalChannelForm
      v-model="formVisible"
      :typeOptions="typeOptions"
      :formatOptions="formatOptions"
      :channelData="channelData"
      @success="onSuccess"
    />

    <DeleteNotificationChannelDialog v-model="deleteChannelDialogVisible" :id="toDeleteChannelId" />
  </div>
</template>

<script>
import Banner from '@design-system/components/Banner.vue';
import Tabs from '@design-system/components/Tabs.vue';
import TabPanels from '@design-system/components/TabPanels.vue';
import TabPanel from '@design-system/components/TabPanel.vue';
import CTitle from '@/components/uikit/CTitle';
import HeadContents from './NotificationHeadContents';
import DataTable from './NotificationDataTable';
import ModalChannelForm from './NotificationDialogChannel';
import DeleteNotificationChannelDialog from './DeleteNotificationChannel.vue';
import { mapActions, mapGetters } from 'vuex';

const types = {
  email: 'Email',
  slack: 'Slack',
  msteams: 'MS Teams',
};

const formats = {
  html: 'HTML',
  text: 'Text',
};

const extractToByType = {
  email: item => item.emails.join('\n'),
  slack: item => item.slackChannel,
  msteams: item => item.msteamsWebhookUrl,
};

const messages = {
  en: {
    Notification: 'Notification',
    Email: 'Email',
    Slack: 'Slack',
    'MS Teams': 'MS Teams',
    Text: 'Text',
    HTML: 'HTML',
    'Please allow the notification.': 'Please allow the notification.',
  },
  ja: {
    Notification: '通知設定',
    Email: 'Eメール',
    Slack: 'Slack',
    'MS Teams': 'MS Teams',
    Text: 'テキスト',
    HTML: 'HTML',
    'Please allow the notification.': '通知設定を有効にしてください。',
  },
};

export default {
  name: 'NotificationSetting',
  components: {
    Banner,
    CTitle,
    HeadContents,
    DataTable,
    ModalChannelForm,
    Tabs,
    TabPanels,
    TabPanel,
    DeleteNotificationChannelDialog,
  },
  i18n: { messages },
  data() {
    return {
      loading: false,
      loadingBudgetAlerts: false,
      formVisible: false,
      channelData: {},
      currentTab: 'channels',
      budgetAlertDialogVisible: false,
      deleteAlertDialogVisible: false,
      toDeleteAlertId: '',
      budgetAlertDialogMode: 'create',
      budgetAlertDialogInitObject: {},
      toDeleteChannelId: '',
      deleteChannelDialogVisible: false,
    };
  },
  async created() {
    await this.fetchData();
    if (this.$route.query.code) {
      const params = JSON.parse(atob(this.$route.query.state));
      await this.saveNotificationChannels({
        ...params,
        slackCode: this.$route.query.code,
      });
      this.$router.replace({ query: {} });
    }
    await this.fetchBudgetAlertsData();
  },
  computed: {
    ...mapGetters('notificationSettings', {
      notificationSettings: 'notificationSettings',
      notificationChannels: 'notificationChannels',
      alerts: 'alerts',
    }),
    typeOptions() {
      return Object.keys(types).map(key => ({
        label: this.$t(types[key]),
        value: key,
      }));
    },
    formatOptions() {
      return Object.keys(formats).map(key => ({
        label: this.$t(formats[key]),
        value: key,
      }));
    },
    tableData() {
      return this.notificationChannels.map(item => ({
        ...item,
        to: extractToByType[item.type](item),
        typeLabel: this.$t(types[item.type]),
        formatLabel: this.$t(formats[item.emailFormat]),
      }));
    },

    tabItems() {
      return [
        {
          label: 'Channels',
          value: 'channels',
        },
        {
          label: 'Budget Alerts',
          value: 'budget-alerts',
        },
      ];
    },
  },
  methods: {
    ...mapActions('notificationSettings', {
      fetchNotificationSettings: 'fetchNotificationSettings',
      fetchNotificationChannels: 'fetchNotificationChannels',
      saveNotificationChannels: 'saveNotificationChannels',
      fetchAlerts: 'fetchAlerts',
    }),

    ...mapActions('accounts', {
      fetchAccounts: 'fetchAccounts',
    }),

    async fetchData() {
      this.loading = true;
      await Promise.all([this.fetchNotificationSettings(), this.fetchNotificationChannels()]);
      this.loading = false;
    },
    async fetchBudgetAlertsData() {
      this.loadingBudgetAlerts = true;
      await Promise.all([this.fetchAlerts(), this.fetchAccounts({ force: true })]);
      this.loadingBudgetAlerts = false;
    },
    openModal(id) {
      this.formVisible = true;
      this.channelData = id ? this.notificationChannels.find(item => item.id === id) : {};
    },
    openDeleteModal(id) {
      this.toDeleteAlertId = id;
      this.deleteAlertDialogVisible = true;
    },
    openBudgetAlertModal(mode, initObject) {
      this.budgetAlertDialogMode = mode;
      this.budgetAlertDialogInitObject = initObject;
      this.budgetAlertDialogVisible = true;
    },
    openDeleteChannelModal(id) {
      this.toDeleteChannelId = id;
      this.deleteChannelDialogVisible = true;
    },
    async onSuccess() {
      this.fetchData();
      this.formVisible = false;
    },
  },
};
</script>
